import type { Post } from '../blogTypes'

export const regulationsInsulation: Post = {
  slug: 'dormer-building-regulations-insulation',
  title: 'Dormer Building Regulations and Insulation: What Inspectors Check',
  metaTitle: 'Dormer Building Regulations and Insulation Requirements Explained',
  description:
    'Understand dormer building regulations: structure, fire escape, insulation U-values, ventilation and stairs. A plain-language checklist before you build.',
  excerpt:
    'A dormer is a new part of your home, so it has to meet current standards. Here is what inspectors look at, and how to insulate properly.',
  category: 'construction-technical',
  image: 'https://images.unsplash.com/photo-1753460133435-bf41927f77f7?auto=format&fit=crop&w=1200&q=75',
  imageAlt: 'Attic with insulation, beams and storage',
  datePublished: '2025-03-05',
  dateModified: '2025-03-20',
  readMinutes: 7,
  blocks: [
    {
      type: 'p',
      text: 'Whether or not you need planning permission, a dormer has to meet building standards. They protect the people who will sleep in the new room and the home beneath it. Inspectors usually visit at key stages, such as framing, insulation and completion.',
    },
    { type: 'h2', text: 'The main areas that are checked' },
    {
      type: 'ul',
      items: [
        '**Structure:** the roof opening needs a correctly sized header or steel beam, and the floor joists must carry the added load.',
        '**Fire safety:** a new habitable room above the first floor usually needs a protected escape route, interlinked smoke alarms and fire-rated doors.',
        '**Stairs and headroom:** access must meet minimum headroom, tread and rise rules.',
        '**Insulation and energy efficiency:** walls and roof must meet current thermal limits.',
        '**Ventilation:** background vents and extractor fans in bathrooms prevent damp.',
        '**Electrical and heating:** work must be certified by a qualified installer.',
      ],
    },
    { type: 'h2', text: 'Dormer insulation: where it matters most' },
    {
      type: 'p',
      text: 'Dormers have three exposed surfaces, the roof and two cheek walls, plus the front wall. That is a lot of heat-loss area for a small room, so insulation quality is crucial. Poorly insulated dormers are the number one cause of cold, damp loft conversions.',
    },
    { type: 'h2', text: 'Typical insulation targets' },
    {
      type: 'ul',
      items: [
        'Roof: aim for U-value about 0.15 W/m2K (around R-38 to R-49 in US terms).',
        'Cheek walls: about 0.18 W/m2K (around R-21 to R-30).',
        'Floor between dormer and room below: fill with mineral wool for sound and fire protection.',
        'Windows: double or triple glazed, low-E, with an air-tight install.',
      ],
    },
    {
      type: 'p',
      text: 'Local codes differ, so confirm exact values with your building department. As a rough planning rule, plan on insulating the entire envelope area, which the [dormer calculator](/calculator) gives you as part of the materials list.',
    },
    { type: 'h2', text: 'Cold roof vs warm roof' },
    {
      type: 'p',
      text: 'A **warm roof** puts rigid insulation above or between the rafters with no ventilated gap, and is the most common modern method for dormer flat roofs. A **cold roof** insulates at ceiling level and keeps a ventilated void above, which suits pitched roofs but needs careful detailing to avoid condensation. Your designer should specify which one fits.',
    },
    { type: 'h2', text: 'Air-tightness and moisture' },
    {
      type: 'p',
      text: 'Insulation only works if air cannot leak around it. Use a vapor control layer on the warm side, tape every joint, and seal around windows and penetrations. Good flashing where the dormer meets the main roof is essential, since this junction is the most likely place for leaks.',
    },
    { type: 'h2', text: 'Questions to ask your builder' },
    {
      type: 'ul',
      items: [
        'Who submits the building permit or notice, and who is responsible for inspections?',
        'What insulation product and thickness are you specifying, and what is its rating?',
        'How will you detail the valley and flashing where the dormer meets the roof?',
        'Will electrical and heating work be certified?',
      ],
    },
    {
      type: 'cta',
      title: 'Plan the materials before you plan the build',
      text: 'See the insulation area, sheathing sheets and cladding quantity for your dormer.',
    },
    {
      type: 'p',
      text: 'Permissions matter too. Read the [dormer planning permission guide](/blog/dormer-planning-permission-guide) to check what applies to your property.',
    },
  ],
}
